import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, Typography, Box, Button, Collapse, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Chip, Divider, CircularProgress,
  Alert
} from '@mui/material';
import {
  AutoAwesome, Close, ExpandMore, ExpandLess, Settings,
  Warning, Info, CheckCircle, TrendingUp, Security
} from '@mui/icons-material';
import api from '../../services/api';
import { brand, text, bg } from '../../theme/colors';

/**
 * Smart Payment Assistant with Hybrid Intelligence
 * - Real data from database
 * - Fraud detection and anomaly alerts
 * - Relevance scoring based on user behavior
 * - Contextual recommendations (time-based, predictive)
 * - Personalized insights
 */
const SmartPaymentAssistant = ({ onDismiss, onOpenSettings }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hiddenCount, setHiddenCount] = useState(0);

  // Fetch smart insights from backend
  useEffect(() => {
    fetchSmartInsights();
  }, []);

  const fetchSmartInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/smart-insights/payment');

      if (response.data.success) {
        setInsights(response.data.data.insights);
        setHiddenCount(response.data.data.hiddenCount || 0);
      }
    } catch (err) {
      console.error('Error fetching smart payment insights:', err);
      setError('Failed to load payment insights');
    } finally {
      setLoading(false);
    }
  };

  // Track user interaction
  const trackInteraction = async (insight, action) => {
    try {
      await api.post('/smart-insights/track', {
        insightType: insight.type,
        insightCategory: insight.category,
        assistantType: 'payment',
        action: action,
        insightData: insight.data
      });
    } catch (err) {
      console.error('Error tracking interaction:', err);
    }
  };

  // Handle insight click
  const handleInsightClick = (insight) => {
    trackInteraction(insight, 'clicked');
    if (insight.actionRoute) {
      navigate(insight.actionRoute);
    }
  };

  // Handle dismiss
  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Get icon for insight
  const getInsightIcon = (category, type) => {
    if (type === 'fraud_alert') return <Security sx={{ color: '#e74c3c' }} />;
    if (category === 'urgent') return <Warning sx={{ color: '#e74c3c' }} />;
    if (category === 'important') return <Info sx={{ color: '#FAA61A' }} />;
    if (type.includes('prediction') || type.includes('forecast')) return <TrendingUp sx={{ color: brand.primary }} />;
    return <CheckCircle sx={{ color: '#43B581' }} />;
  };

  // Get color for insight
  const getInsightColor = (category, type) => {
    if (category === 'urgent' || type === 'fraud_alert') return '#e74c3c';
    if (category === 'important') return '#FAA61A';
    return brand.primary;
  };

  // Group insights by category
  const groupedInsights = {
    urgent: insights.filter(i => i.category === 'urgent'),
    important: insights.filter(i => i.category === 'important'),
    suggestion: insights.filter(i => i.category === 'suggestion')
  };

  const totalInsights = insights.length;

  if (dismissed) {
    return null;
  }

  if (loading) {
    return (
      <Card
        sx={{
          borderRadius: 2,
          mb: 3,
          border: '2px solid #43B581',
          bgcolor: '#43B58110'
        }}
      >
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={30} sx={{ color: '#43B581' }} />
          <Typography variant="body2" sx={{ ml: 2, color: text.secondary }}>
            Analyzing payments...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }} onClose={handleDismiss}>
        {error}
      </Alert>
    );
  }

  if (totalInsights === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        mb: 3,
        border: '2px solid #43B581',
        bgcolor: '#43B58110'
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ color: '#43B581', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              💰 AI Payment Assistant
            </Typography>
            <Chip
              label={`${totalInsights} insight${totalInsights > 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: '#43B581', color: 'white', fontWeight: 600 }}
            />
            {hiddenCount > 0 && (
              <Chip
                label={`+${hiddenCount} more`}
                size="small"
                variant="outlined"
                sx={{ borderColor: '#43B581', color: '#43B581', fontWeight: 600 }}
              />
            )}
          </Box>
          <Box>
            {onOpenSettings && (
              <IconButton size="small" onClick={onOpenSettings} title="AI Settings">
                <Settings />
              </IconButton>
            )}
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <IconButton size="small" onClick={handleDismiss}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
            AI-powered payment monitoring, fraud detection, and cash flow insights:
          </Typography>

          {/* Urgent Actions */}
          {groupedInsights.urgent.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e74c3c', mb: 1 }}>
                🔴 ACTION REQUIRED ({groupedInsights.urgent.length})
              </Typography>
              <List sx={{ bgcolor: 'white', borderRadius: 1 }}>
                {groupedInsights.urgent.map((insight, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderLeft: `4px solid ${getInsightColor(insight.category, insight.type)}`,
                      mb: 1,
                      borderRadius: 1,
                      cursor: insight.action ? 'pointer' : 'default',
                      '&:hover': insight.action ? {
                        bgcolor: bg.subtle
                      } : {}
                    }}
                    onClick={() => insight.action && handleInsightClick(insight)}
                  >
                    <ListItemIcon>{getInsightIcon(insight.category, insight.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insight.text}
                        </Typography>
                      }
                      secondary={insight.subtitle}
                    />
                    {insight.action && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsightClick(insight);
                        }}
                        sx={{
                          textTransform: 'none',
                          borderColor: getInsightColor(insight.category, insight.type),
                          color: getInsightColor(insight.category, insight.type),
                          fontWeight: 600
                        }}
                      >
                        {insight.action}
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Important Items */}
          {groupedInsights.important.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#FAA61A', mb: 1 }}>
                🟡 IMPORTANT ({groupedInsights.important.length})
              </Typography>
              <List sx={{ bgcolor: 'white', borderRadius: 1 }}>
                {groupedInsights.important.map((insight, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderLeft: `4px solid ${getInsightColor(insight.category, insight.type)}`,
                      mb: 1,
                      borderRadius: 1,
                      cursor: insight.action ? 'pointer' : 'default',
                      '&:hover': insight.action ? {
                        bgcolor: bg.subtle
                      } : {}
                    }}
                    onClick={() => insight.action && handleInsightClick(insight)}
                  >
                    <ListItemIcon>{getInsightIcon(insight.category, insight.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insight.text}
                        </Typography>
                      }
                      secondary={insight.subtitle}
                    />
                    {insight.action && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsightClick(insight);
                        }}
                        sx={{
                          textTransform: 'none',
                          borderColor: getInsightColor(insight.category, insight.type),
                          color: getInsightColor(insight.category, insight.type),
                          fontWeight: 600
                        }}
                      >
                        {insight.action}
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Recommendations */}
          {groupedInsights.suggestion.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: brand.primary, mb: 1 }}>
                💡 RECOMMENDATIONS ({groupedInsights.suggestion.length})
              </Typography>
              <List sx={{ bgcolor: 'white', borderRadius: 1 }}>
                {groupedInsights.suggestion.map((insight, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderLeft: `4px solid ${getInsightColor(insight.category, insight.type)}`,
                      mb: index < groupedInsights.suggestion.length - 1 ? 1 : 0,
                      borderRadius: 1,
                      cursor: insight.action ? 'pointer' : 'default',
                      '&:hover': insight.action ? {
                        bgcolor: bg.subtle
                      } : {}
                    }}
                    onClick={() => insight.action && handleInsightClick(insight)}
                  >
                    <ListItemIcon>{getInsightIcon(insight.category, insight.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insight.text}
                        </Typography>
                      }
                      secondary={insight.subtitle}
                    />
                    {insight.action && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsightClick(insight);
                        }}
                        sx={{
                          textTransform: 'none',
                          color: getInsightColor(insight.category, insight.type),
                          fontWeight: 600
                        }}
                      >
                        {insight.action} →
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: text.secondary }}>
              🧠 AI-powered insights personalized for you
            </Typography>
            <Button
              size="small"
              onClick={handleDismiss}
              sx={{ textTransform: 'none', color: text.secondary }}
            >
              Dismiss
            </Button>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default SmartPaymentAssistant;
